import React, { useState } from "react";
import { Avatar, AvatarImage } from "../../components/ui";
import { statuses, getAdminOrderUrl } from "../../constants";
import Context from "../../Context";
import { getPaymentAccount } from "../../util/bankUtils";

// Icons
import {
  FaUser,
  FaPhone,
  FaLocationDot,
  FaBuildingColumns,
  FaCoins,
  FaWallet,
  FaCopy,
  FaCheck,
  FaArrowUpRightFromSquare,
  FaShieldHalved,
  FaCakeCandles,
  FaGlobe,
  FaArrowRightToBracket,
  FaIdCard,
  FaEnvelope,
  FaCartShopping,
} from "react-icons/fa6";
import { FaLine } from "react-icons/fa";

export const UserPreview = ({ user = {} }) => {
  const { callApi } = Context();
  const [copiedField, setCopiedField] = useState(null);
  const [loggingIn, setLoggingIn] = useState(false);

  const safeUser = user || {};
  const activeAcc = getPaymentAccount(safeUser, safeUser?.paymentMethod);

  // Copy Helper
  const handleCopy = (text, fieldName) => {
    if (!text) return;
    navigator.clipboard.writeText(String(text));
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  // Login as user action
  const handleLoginAsUser = async () => {
    if (!safeUser?.userId) return;
    setLoggingIn(true);
    try {
      const result = await callApi("users-addAdminToken", {
        userId: safeUser.userId,
      });
      if (result?.adminToken) {
        window.open(getAdminOrderUrl(result.adminToken), "_blank");
      }
    } catch (err) {
      console.error("Login as user error:", err);
    } finally {
      setLoggingIn(false);
    }
  };

  // Payment method label helper
  const getPaymentMethodLabel = (method) => {
    switch (method) {
      case "LAO_BANK":
        return "ธนาคาร สปป.ลาว (LAK)";
      case "ALIPAY":
        return "Alipay (CNY)";
      case "CREDIT_CARD":
        return "บัตรเครดิต/เดบิต";
      default:
        return "ธนาคารไทย (THB)";
    }
  };

  // User Status Badge Helper
  const getUserStatusBadge = (status) => {
    if (status === statuses.REMOVED) {
      return (
        <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-red-100 text-red-700 border border-red-200">
          ถูกระงับ/ลบ
        </span>
      );
    }
    if (status === statuses.INACTIVE) {
      return (
        <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-amber-100 text-amber-800 border border-amber-200">
          ปิดใช้งาน (Inactive)
        </span>
      );
    }
    return (
      <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
        ปกติ (Active)
      </span>
    );
  };

  if (!user) {
    return (
      <div className="bg-white w-full rounded-md shadow-md border border-gray-200 p-8 flex flex-col items-center justify-center gap-3">
        <div className="animate-spin rounded-full h-8 w-8 border-3 border-rose-500 border-t-transparent"></div>
        <p className="text-xs font-semibold text-gray-400">กำลังดึงข้อมูลลูกค้า...</p>
      </div>
    );
  }

  const fullNameTh = `${safeUser?.firstName || ""} ${safeUser?.lastName || ""}`.trim();
  const fullNameEn = `${safeUser?.firstNameEn || ""} ${safeUser?.lastNameEn || ""}`.trim();
  const fullName = fullNameTh || fullNameEn || "ไม่ระบุชื่อ";

  const shippingAddress = [
    safeUser?.address?.addr,
    safeUser?.address?.subdistrict,
    safeUser?.address?.district,
    safeUser?.address?.province,
    safeUser?.address?.zipcode,
  ]
    .filter(Boolean)
    .join(" ") || safeUser?.address?.addr || "-";

  const idCardAddress = [
    safeUser?.address?.addrId,
    safeUser?.address?.provinceId,
    safeUser?.address?.zipcodeId,
  ]
    .filter(Boolean)
    .join(" ") || safeUser?.address?.addrId || "";

  return (
    <div className="bg-white w-full rounded-md shadow-md border border-gray-200 overflow-hidden">
      {/* ── Top Header Profile Bar ────────────────────────────────────────── */}
      <div className="p-4 sm:p-5 border-b border-gray-100 bg-gray-50/70 flex flex-col md:flex-row md:items-center justify-between gap-4">
        {/* Left: Avatar & Identity Summary */}
        <div className="flex items-center gap-3.5 min-w-0">
          <div className="relative shrink-0">
            <Avatar className="w-14 h-14 rounded-full border-2 border-white shadow-sm ring-1 ring-gray-200">
              <AvatarImage
                src={safeUser?.profileImage || "https://github.com/shadcn.png"}
                alt={fullName}
                className="object-cover"
              />
            </Avatar>
            <div
              className={`w-3.5 h-3.5 rounded-full border-2 border-white absolute bottom-0 right-0 shadow-xs ${safeUser?.status === statuses.REMOVED
                ? "bg-rose-500"
                : safeUser?.status === statuses.INACTIVE
                  ? "bg-amber-500"
                  : "bg-emerald-500"
                }`}
              title={`สถานะบัญชี: ${safeUser?.status || "active"}`}
            ></div>
          </div>

          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-base sm:text-lg font-bold text-gray-900 leading-tight">
                {fullName}
              </h3>
              {fullNameEn && fullNameTh && (
                <span className="text-xs font-semibold text-gray-400">
                  ({fullNameEn})
                </span>
              )}
              <div className="flex items-center gap-1 bg-rose-50 px-2 py-0.5 rounded border border-rose-200">
                <span className="font-mono text-xs font-bold text-rose-600">
                  #{safeUser?.userId || "-"}
                </span>
                {safeUser?.userId && (
                  <button
                    onClick={() => handleCopy(safeUser.userId, "userIdHeader")}
                    className="text-gray-400 hover:text-rose-600 ml-0.5"
                    title="คัดลอก User ID"
                  >
                    {copiedField === "userIdHeader" ? (
                      <FaCheck className="text-emerald-600 text-[10px]" />
                    ) : (
                      <FaCopy className="text-[10px]" />
                    )}
                  </button>
                )}
              </div>
              {getUserStatusBadge(safeUser?.status)}
              {safeUser?.verifiedId && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold bg-blue-50 text-blue-700 border border-blue-200">
                  <FaShieldHalved className="text-[10px]" /> ยืนยันตัวตนแล้ว
                </span>
              )}
            </div>

            <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-500 font-semibold flex-wrap">
              <span>เบอร์: <b className="text-gray-800">{safeUser?.phone || "-"}</b></span>
              {safeUser?.lineTitle && (
                <span className="flex items-center gap-1 text-emerald-700">
                  <FaLine className="text-emerald-500 text-xs" />
                  <span>ชื่อไลน์: <b>{safeUser.lineTitle}</b></span>
                </span>
              )}
              {safeUser?.lineId && (
                <span className="flex items-center gap-1 text-emerald-700">
                  <span>ID: <b className="font-mono">{safeUser.lineId}</b></span>
                </span>
              )}
              {safeUser?.email && (
                <span className="text-gray-600 flex items-center gap-1">
                  <FaEnvelope className="text-[11px] text-gray-400" />
                  <span>{safeUser.email}</span>
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Right: Quick Action Buttons */}
        <div className="flex items-center gap-2 flex-wrap self-start md:self-auto shrink-0">
          <button
            onClick={() => window.open(`/user/${safeUser.userId}`, "_blank")}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold rounded-md bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:text-navy shadow-2xs transition-all"
            title="เปิดหน้ารายละเอียดลูกค้าแบบเต็ม"
          >
            <FaUser className="text-[11px] text-gray-500" />
            <span>ดูโปรไฟล์เต็ม</span>
            <FaArrowUpRightFromSquare className="text-[9px] text-gray-400 ml-0.5" />
          </button>

          <button
            disabled={loggingIn || !safeUser?.userId}
            onClick={handleLoginAsUser}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold rounded-md bg-navy text-white hover:bg-slate-800 disabled:opacity-50 shadow-2xs transition-all"
            title="เข้าสู่ระบบในมุมมองลูกค้า (Impersonate Login)"
          >
            <FaArrowRightToBracket className="text-[11px]" />
            <span>{loggingIn ? "กำลังเข้าสู่ระบบ..." : "ล็อกอินเป็นลูกค้า"}</span>
          </button>
        </div>
      </div>

      {/* ── Complete Customer Information Grid (4-Column Layout) ───────── */}
      <div className="p-4 sm:p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 divide-y lg:divide-y-0 lg:divide-x divide-gray-100">
        {/* Column 1: Contact & Identification */}
        <div className="space-y-2 lg:pr-3">
          <div className="flex items-center gap-1.5 text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
            <FaPhone className="text-rose-500 text-[11px]" />
            <span>ข้อมูลติดต่อ & ข้อมูลส่วนตัว</span>
          </div>

          {/* Phone */}
          <div className="flex items-center justify-between p-2 bg-gray-50/70 rounded-md border border-gray-200/70 text-xs">
            <span className="font-semibold text-gray-500">เบอร์โทรศัพท์</span>
            <div className="flex items-center gap-1.5">
              <span className="font-mono font-bold text-gray-900">{safeUser?.phone || "-"}</span>
              {safeUser?.phone && (
                <button
                  onClick={() => handleCopy(safeUser.phone, "phone")}
                  className="p-0.5 text-gray-400 hover:text-gray-700 transition-colors"
                  title="คัดลอกเบอร์โทร"
                >
                  {copiedField === "phone" ? (
                    <FaCheck className="text-emerald-600 text-[10px]" />
                  ) : (
                    <FaCopy className="text-[10px]" />
                  )}
                </button>
              )}
            </div>
          </div>

          {/* LINE Display Name */}
          <div className="flex items-center justify-between p-2 bg-gray-50/70 rounded-md border border-gray-200/70 text-xs">
            <span className="font-semibold text-gray-500 flex items-center gap-1">
              <FaLine className="text-emerald-500 text-xs" /> ชื่อไลน์
            </span>
            <span className="font-bold text-gray-900 truncate max-w-[130px]" title={safeUser?.lineTitle}>
              {safeUser?.lineTitle || "-"}
            </span>
          </div>

          {/* LINE ID (Separate Row) */}
          <div className="flex items-center justify-between p-2 bg-gray-50/70 rounded-md border border-gray-200/70 text-xs">
            <span className="font-semibold text-gray-500 flex items-center gap-1">
              <FaLine className="text-emerald-500 text-xs" /> ไอดีไลน์
            </span>
            <div className="flex items-center gap-1.5">
              <span className="font-mono font-bold text-gray-900 truncate max-w-[120px]" title={safeUser?.lineId}>
                {safeUser?.lineId || "-"}
              </span>
              {safeUser?.lineId && (
                <button
                  onClick={() => handleCopy(safeUser.lineId, "lineId")}
                  className="p-0.5 text-gray-400 hover:text-gray-700 transition-colors"
                  title="คัดลอก LINE ID"
                >
                  {copiedField === "lineId" ? (
                    <FaCheck className="text-emerald-600 text-[10px]" />
                  ) : (
                    <FaCopy className="text-[10px]" />
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Email (if available) */}
          {safeUser?.email && (
            <div className="flex items-center justify-between p-2 bg-gray-50/70 rounded-md border border-gray-200/70 text-xs">
              <span className="font-semibold text-gray-500">อีเมล</span>
              <span className="font-semibold text-gray-800 truncate max-w-[140px]" title={safeUser.email}>
                {safeUser.email}
              </span>
            </div>
          )}

          {/* Nationality & DOB */}
          <div className="grid grid-cols-2 gap-2">
            <div className="p-2 bg-gray-50/70 rounded-md border border-gray-200/70 text-xs">
              <span className="font-semibold text-gray-400 text-[11px] block">
                <FaGlobe className="inline text-[10px] mr-1" /> สัญชาติ
              </span>
              <span className="font-bold text-gray-800">{safeUser?.nationality || "ไทย"}</span>
            </div>
            <div className="p-2 bg-gray-50/70 rounded-md border border-gray-200/70 text-xs">
              <span className="font-semibold text-gray-400 text-[11px] block">
                <FaCakeCandles className="inline text-[10px] mr-1" /> วันเกิด
              </span>
              <span className="font-bold text-gray-800">{safeUser?.dob || "-"}</span>
            </div>
          </div>
        </div>

        {/* Column 2: Addresses & ID Card */}
        <div className="space-y-2 pt-4 lg:pt-0 lg:px-3">
          <div className="flex items-center gap-1.5 text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
            <FaLocationDot className="text-rose-500 text-[11px]" />
            <span>ที่อยู่จัดส่ง & ข้อมูลบัตร</span>
          </div>

          {/* Citizen ID Card Number (if present) */}
          {safeUser?.idCard && (
            <div className="flex items-center justify-between p-2 bg-gray-50/70 rounded-md border border-gray-200/70 text-xs">
              <span className="font-semibold text-gray-500 flex items-center gap-1">
                <FaIdCard className="text-[11px]" /> เลขบัตรประชาชน
              </span>
              <div className="flex items-center gap-1.5">
                <span className="font-mono font-bold text-gray-900">{safeUser.idCard}</span>
                <button
                  onClick={() => handleCopy(safeUser.idCard, "idCard")}
                  className="p-0.5 text-gray-400 hover:text-gray-700 transition-colors"
                  title="คัดลอกเลขบัตรประชาชน"
                >
                  {copiedField === "idCard" ? (
                    <FaCheck className="text-emerald-600 text-[10px]" />
                  ) : (
                    <FaCopy className="text-[10px]" />
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Shipping Address */}
          <div className="p-2.5 bg-gray-50/70 rounded-md border border-gray-200/70 text-xs space-y-1">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-gray-500 flex items-center gap-1">
                <FaLocationDot className="text-rose-500 text-[10px]" /> ที่อยู่จัดส่ง
              </span>
              {shippingAddress !== "-" && (
                <button
                  onClick={() => handleCopy(shippingAddress, "shippingAddress")}
                  className="text-gray-400 hover:text-gray-700 text-[10px] inline-flex items-center gap-1"
                  title="คัดลอกที่อยู่จัดส่ง"
                >
                  {copiedField === "shippingAddress" ? (
                    <span className="text-emerald-600 font-bold">คัดลอกแล้ว</span>
                  ) : (
                    <>
                      <FaCopy className="text-[10px]" /> คัดลอก
                    </>
                  )}
                </button>
              )}
            </div>
            <span className="font-semibold text-gray-800 block leading-relaxed" title={shippingAddress}>
              {shippingAddress}
            </span>
          </div>

          {/* ID Card Address (if available and different) */}
          {idCardAddress && idCardAddress !== shippingAddress && (
            <div className="p-2.5 bg-gray-50/70 rounded-md border border-gray-200/70 text-xs space-y-1">
              <span className="font-semibold text-gray-500 flex items-center gap-1">
                <FaIdCard className="text-gray-400 text-[10px]" /> ที่อยู่ตามบัตรประชาชน
              </span>
              <span className="font-semibold text-gray-700 block leading-relaxed text-[11px]" title={idCardAddress}>
                {idCardAddress}
              </span>
            </div>
          )}
        </div>

        {/* Column 3: Bank Account & Payment Method */}
        <div className="space-y-2 pt-4 lg:pt-0 lg:px-3">
          <div className="flex items-center gap-1.5 text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
            <FaBuildingColumns className="text-rose-500 text-[11px]" />
            <span>บัญชีธนาคาร</span>
          </div>

          {/* Main Payment Channel */}
          <div className="flex items-center justify-between p-2 bg-gray-50/70 rounded-md border border-gray-200/70 text-xs">
            <span className="font-semibold text-gray-500">ช่องทางหลัก</span>
            <span className="font-bold text-gray-900">
              {getPaymentMethodLabel(safeUser?.paymentMethod)}
            </span>
          </div>

          {/* Bank Name */}
          <div className="flex items-center justify-between p-2 bg-gray-50/70 rounded-md border border-gray-200/70 text-xs">
            <span className="font-semibold text-gray-500">ธนาคาร</span>
            <span className="font-bold text-gray-900">{activeAcc?.name || safeUser?.bank?.name || "-"}</span>
          </div>

          {/* Bank Account Number */}
          <div className="flex items-center justify-between p-2 bg-gray-50/70 rounded-md border border-gray-200/70 text-xs">
            <span className="font-semibold text-gray-500">เลขที่บัญชี</span>
            <div className="flex items-center gap-1.5">
              <span className="font-mono font-bold text-gray-900">
                {activeAcc?.accNo || safeUser?.bank?.accNo || "-"}
              </span>
              {(activeAcc?.accNo || safeUser?.bank?.accNo) && (
                <button
                  onClick={() => handleCopy(activeAcc?.accNo || safeUser?.bank?.accNo, "accNo")}
                  className="p-0.5 text-gray-400 hover:text-gray-700 transition-colors"
                  title="คัดลอกเลขบัญชี"
                >
                  {copiedField === "accNo" ? (
                    <FaCheck className="text-emerald-600 text-[10px]" />
                  ) : (
                    <FaCopy className="text-[10px]" />
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Bank Account Name */}
          <div className="flex items-center justify-between p-2 bg-gray-50/70 rounded-md border border-gray-200/70 text-xs">
            <span className="font-semibold text-gray-500">ชื่อบัญชี</span>
            <span className="font-bold text-gray-900 truncate max-w-[130px]" title={activeAcc?.accName || safeUser?.bank?.accName}>
              {activeAcc?.accName || safeUser?.bank?.accName || "-"}
            </span>
          </div>

          {/* Lao Bank or Alipay Account (if configured) */}
          {safeUser?.laoBank?.accNo && safeUser?.paymentMethod !== "LAO_BANK" && (
            <div className="flex items-center justify-between p-2 bg-gray-50/70 rounded-md border border-gray-200/70 text-xs">
              <span className="font-semibold text-gray-500">ธนาคารลาว</span>
              <span className="font-mono font-bold text-gray-800">{safeUser.laoBank.accNo}</span>
            </div>
          )}

          {safeUser?.alipayAcc && safeUser?.paymentMethod !== "ALIPAY" && (
            <div className="flex items-center justify-between p-2 bg-gray-50/70 rounded-md border border-gray-200/70 text-xs">
              <span className="font-semibold text-gray-500">Alipay ID</span>
              <span className="font-mono font-bold text-gray-800">{safeUser.alipayAcc}</span>
            </div>
          )}
        </div>

        {/* Column 4: Points, Cash Balance & System Preferences */}
        <div className="space-y-2.5 pt-4 lg:pt-0 lg:pl-3">
          <div className="flex items-center gap-1.5 text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
            <FaCoins className="text-rose-500 text-[11px]" />
            <span>สิทธิประโยชน์ & ยอดเงิน</span>
          </div>

          {/* Buy Point Box */}
          <div className="p-3 bg-amber-50/70 rounded-md border border-amber-200/80 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-md bg-amber-500 text-white flex items-center justify-center font-bold text-sm shadow-xs">
                <FaCoins />
              </div>
              <div>
                <span className="text-[11px] font-semibold text-amber-800 block">คะแนนสะสม (Points)</span>
                <span className="text-lg font-black text-amber-900 leading-none">
                  {Number(safeUser?.buyPoint || 0).toLocaleString()}
                </span>
                <span className="text-[10px] font-semibold text-amber-800 block">หมดอายุเดือน {safeUser.pointExpireMonth}</span>
              </div>
            </div>
          </div>

          {/* Buy Cash Box */}
          <div className="p-3 bg-emerald-50/70 rounded-md border border-emerald-200/80 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-md bg-emerald-600 text-white flex items-center justify-center font-bold text-sm shadow-xs">
                <FaWallet />
              </div>
              <div>
                <span className="text-[11px] font-semibold text-emerald-800 block">รายได้ชวนเพื่อน (Cash)</span>
                <span className="text-lg font-black text-emerald-900 leading-none">
                  ฿{Number(safeUser?.buyCash || 0).toLocaleString()} <span className="text-xs font-semibold text-emerald-700">บาท</span>
                </span>
              </div>
            </div>
          </div>

          {/* Cart Expiration Time or Registration Date */}
          {safeUser?.cartTime && (
            <div className="flex items-center justify-between p-2 bg-gray-50/70 rounded-md border border-gray-200/70 text-xs">
              <span className="font-semibold text-gray-500 flex items-center gap-1">
                <FaCartShopping className="text-gray-400 text-[10px]" /> เวลาจองตะกร้า
              </span>
              <span className="font-bold text-gray-800">
                {safeUser.cartTime} นาที
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserPreview;
